import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Search, Loader2, Mail, X, Save, Shield, KeyRound, Trash2, Sparkles, Ban, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

import RichTextEditor from "@/components/RichTextEditor";
const ROLES = ["discovery", "community", "enhancer", "vendor", "speaker", "musician", "staff", "council", "admin"];
const roleBadge = (r) => ({
  admin: "bg-[#144F75] text-white", staff: "bg-[#144F75]/15 text-[#144F75]", council: "bg-[#8A6FB0]/15 text-[#8A6FB0]",
  enhancer: "bg-[#5C8A72]/15 text-[#5C8A72]", community: "bg-[#D98E73]/15 text-[#B5794E]",
}[r] || "bg-[#E5E0D8] text-[#4A5D6B]");

export default function UsersPanel() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [modal, setModal] = useState(null); // {} new | user edit

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", q, roleFilter],
    queryFn: () => api.get(`/admin/users?q=${encodeURIComponent(q)}&role=${roleFilter}`).then((r) => r.data),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const deleteOne = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      setSelected((s) => { const n = new Set(s); n.delete(u.id); return n; });
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected user(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data: r } = await api.post("/admin/users/bulk-delete", { ids });
      toast.success(`Deleted ${r.deleted} user(s)`);
      setSelected(new Set());
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const sendReset = async (u) => {
    try {
      const { data: r } = await api.post(`/admin/users/${u.id}/send-reset`, { origin: window.location.origin });
      toast.success(r.sent ? `Invite emailed to ${u.email}` : "Email not sent (add a Resend key in Integrations)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await api.put(`/admin/users/${u.id}`, { role });
      toast.success(`${u.name || u.email} → ${role}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const users = data?.users || [];

  return (
    <div data-testid="admin-users-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Users size={22} className="text-[#144F75]" /> Members & Enhancers</h2>
          <p className="text-sm text-[#4A5D6B]">{data?.total ?? 0} total · manage roles, membership, and permissions.</p>
        </div>
        <button onClick={() => setModal({})} data-testid="admin-add-user" className="eyw-btn !py-2 text-sm"><Plus size={16} /> Add user</button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5" data-testid="bulk-delete-bar">
          <span className="text-sm text-red-700">{selected.size} selected</span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="text-xs text-[#4A5D6B] hover:underline">Clear</button>
            <button onClick={deleteSelected} disabled={deleting} data-testid="bulk-delete-btn" className="eyw-btn !py-1.5 !px-3 text-xs !bg-red-600 hover:!bg-red-700 disabled:opacity-60">
              {deleting ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} />} Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" placeholder="Search name, email, phone…" className="eyw-input w-full pl-9" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="admin-users-role-filter" className="eyw-input">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r} {data?.counts?.[r] != null ? `(${data.counts[r]})` : ""}</option>)}
        </select>
      </div>

      {isLoading ? <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p> : (
        <>
        <div className="eyw-card overflow-hidden hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F1EB] text-[#4A5D6B]">
              <tr><th className="px-3 py-3 w-8"></th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3 hidden md:table-cell">Phone</th><th className="text-left px-4 py-3 hidden lg:table-cell">Location</th><th className="text-left px-4 py-3">Role / membership</th><th className="text-left px-4 py-3 hidden lg:table-cell">Joined</th><th className="text-left px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-[#E5E0D8]">
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className={`hover:bg-[#F9F7F3] ${selected.has(u.id) ? "bg-red-50/60" : ""}`}>
                  <td className="px-3 py-3"><input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSel(u.id)} data-testid={`admin-user-select-${u.id}`} className="accent-[#144F75]" /></td>
                  <td className="px-4 py-3 text-[#1A242B]">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-[#4A5D6B]">{u.email}</td>
                  <td className="px-4 py-3 text-[#4A5D6B] hidden md:table-cell">{u.phone || "—"}</td>
                  <td className="px-4 py-3 text-[#4A5D6B] hidden lg:table-cell">{u.location || [u.city, u.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`admin-user-role-${u.id}`}
                      className={`text-xs px-2.5 py-1 rounded-full border-0 outline-none cursor-pointer font-medium ${roleBadge(u.role)}`}>
                      {ROLES.map((r) => <option key={r} value={r} className="bg-white text-[#1A242B]">{r}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-[#8FA89B] text-xs hidden lg:table-cell">{String(u.created_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-3">{u.has_password ? <span className="text-xs text-[#5C8A72]">Active</span> : <span className="text-xs text-[#B5794E]">Invite pending</span>}{u.active === false && <span className="text-xs text-red-500 ml-1">· disabled</span>}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <a href={`/crm/member/${u.id}`} target="_blank" rel="noreferrer" data-testid={`admin-user-360-${u.id}`} title="Full member profile (360°)" className="text-[#5C8A72] hover:underline text-xs font-medium mr-3">360°</a>
                    <button onClick={() => sendReset(u)} data-testid={`admin-user-reset-${u.id}`} title="Send set/reset password link" className="text-[#144F75] hover:text-[#0d3a57] mr-3"><KeyRound size={15} /></button>
                    <button onClick={() => setModal(u)} data-testid={`admin-user-edit-${u.id}`} className="text-[#144F75] hover:underline text-xs font-medium mr-3">Manage</button>
                    <button onClick={() => deleteOne(u)} data-testid={`admin-user-delete-${u.id}`} title="Delete user" className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#4A5D6B]">No users match your search.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile: member cards */}
        <div className="md:hidden space-y-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-card-${u.id}`} className={`rounded-xl border bg-white p-4 ${selected.has(u.id) ? "border-red-300 bg-red-50/40" : "border-[#E5E0D8]"}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSel(u.id)} data-testid={`admin-user-select-${u.id}`} className="accent-[#144F75] mt-1" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#1A242B] break-words">{u.name || "—"}</p>
                  <p className="text-sm text-[#4A5D6B] break-all">{u.email}</p>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Phone</dt><dd className="text-[#1A242B]">{u.phone || "—"}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Location</dt><dd className="text-[#1A242B] break-words">{u.location || [u.city, u.state].filter(Boolean).join(", ") || "—"}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Joined</dt><dd className="text-[#1A242B]">{String(u.created_at || "").slice(0, 10) || "—"}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wide text-[#8FA89B]">Status</dt><dd>{u.has_password ? <span className="text-xs text-[#5C8A72]">Active</span> : <span className="text-xs text-[#B5794E]">Invite pending</span>}{u.active === false && <span className="text-xs text-red-500 ml-1">· disabled</span>}</dd></div>
              </dl>
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-wide text-[#8FA89B] block mb-1">Role / membership</label>
                <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`admin-user-role-${u.id}`}
                  className={`text-xs px-2.5 py-1.5 rounded-full border-0 outline-none font-medium ${roleBadge(u.role)}`}>
                  {ROLES.map((r) => <option key={r} value={r} className="bg-white text-[#1A242B]">{r}</option>)}
                </select>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-[#E5E0D8] pt-3">
                <button onClick={() => setModal(u)} data-testid={`admin-user-edit-${u.id}`} className="eyw-btn !py-2 !px-4 text-xs flex-1 justify-center">Manage</button>
                <button onClick={() => sendReset(u)} data-testid={`admin-user-reset-${u.id}`} title="Send set/reset password link" className="grid place-items-center h-9 w-9 rounded-lg bg-[#144F75]/10 text-[#144F75]"><KeyRound size={16} /></button>
                <button onClick={() => deleteOne(u)} data-testid={`admin-user-delete-${u.id}`} title="Delete user" className="grid place-items-center h-9 w-9 rounded-lg bg-red-50 text-red-600"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
          {users.length === 0 && <p className="text-center text-[#4A5D6B] py-8 rounded-xl border border-[#E5E0D8] bg-white">No users match your search.</p>}
        </div>
        </>
      )}

      {modal && <UserModal user={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={refresh} />}
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const editing = !!user;
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "", role: user?.role || "discovery",
    phone: user?.phone || "", headline: user?.headline || "", location: user?.location || "",
    password: "", send_invite: true, can_publish: user?.can_publish || false,
    show_in_directory: user?.show_in_directory !== false, active: user?.active !== false,
    is_certified_guide: user?.is_certified_guide || false, guide_bio: user?.guide_bio || "",
    hourly_rate: user?.hourly_rate ?? 0, staff_location_id: user?.staff_location_id || "",
  });
  const { data: centers = [] } = useQuery({ queryKey: ["public-locations-list"], queryFn: () => api.get("/locations").then((r) => r.data) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/users/${user.id}`, {
          name: form.name, role: form.role, phone: form.phone, headline: form.headline,
          location: form.location, can_publish: form.can_publish, show_in_directory: form.show_in_directory, active: form.active,
          is_certified_guide: form.is_certified_guide, guide_bio: form.guide_bio,
          hourly_rate: Number(form.hourly_rate) || 0,
          ...(form.role === "staff" ? { staff_location_id: form.staff_location_id } : {}),
        });
        toast.success("User updated");
      } else {
        const { data } = await api.post("/admin/users", { ...form, origin: window.location.origin });
        toast.success(form.password ? "User created" : (data.invite_sent ? "User created — invite emailed" : "User created (no email sent — add a Resend key)"));
      }
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inp = "eyw-input w-full";
  return (
    <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="admin-user-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <p className="eyw-label mb-1 flex items-center gap-2"><Shield size={14} /> {editing ? "Manage user" : "Add user"}</p>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-5">{editing ? form.name || form.email : "New member"}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Email</label>
            <input value={form.email} disabled={editing} onChange={(e) => set("email", e.target.value)} data-testid="user-email" className={`${inp} ${editing ? "opacity-60" : ""}`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#4A5D6B] mb-1 block">Name</label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name" className={inp} /></div>
            <div><label className="text-xs text-[#4A5D6B] mb-1 block">Role / membership</label>
              <select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="user-role" className={inp}>
                {ROLES.filter((r) => editing || r !== "enhancer").map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              {!editing && <p className="text-[11px] text-[#8FA89B] mt-1">Enhancers join via the Enhancer application (you approve it) and stay the owner of their profile.</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#4A5D6B] mb-1 block">Phone</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="user-phone" className={inp} /></div>
            <div><label className="text-xs text-[#4A5D6B] mb-1 block">Location</label><input value={form.location} onChange={(e) => set("location", e.target.value)} className={inp} /></div>
          </div>
          <div><label className="text-xs text-[#4A5D6B] mb-1 block">Headline (enhancers)</label><input value={form.headline} onChange={(e) => set("headline", e.target.value)} className={inp} /></div>

          {!editing && (
            <div>
              <label className="text-xs text-[#4A5D6B] mb-1 block">Password (optional — leave blank to email a set-password invite)</label>
              <input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password" placeholder="Auto-invite if blank" className={inp} />
            </div>
          )}

          <label className="flex items-center justify-between text-sm text-[#1A242B]">
            <span className="flex items-center gap-1.5"><Mail size={14} /> Can publish media (podcasts/vlogs/courses)</span>
            <input type="checkbox" checked={form.can_publish} onChange={(e) => set("can_publish", e.target.checked)} data-testid="user-can-publish" />
          </label>
          <label className="flex items-center justify-between text-sm text-[#1A242B]">
            <span>List in public directory</span>
            <input type="checkbox" checked={form.show_in_directory} onChange={(e) => set("show_in_directory", e.target.checked)} />
          </label>
          {editing && (
            <label className="flex items-center justify-between text-sm text-[#1A242B]">
              <span>Account active</span>
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="user-active" />
            </label>
          )}
          {editing && (
            <>
              <label className="flex items-center justify-between text-sm text-[#1A242B]">
                <span className="flex items-center gap-1.5"><Sparkles size={14} className="text-[#D98E73]" /> Certified GAUGE guide</span>
                <input type="checkbox" checked={form.is_certified_guide} onChange={(e) => set("is_certified_guide", e.target.checked)} data-testid="user-certified-guide" />
              </label>
              {form.is_certified_guide && (
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Guide bio (shown to members choosing a guide)</label>
                  <RichTextEditor value={form.guide_bio} onChange={(v) => set("guide_bio", v)} testid="user-guide-bio" placeholder="Guide bio" minHeight={90} enableBlocks={false} media={false} />
                </div>
              )}
            </>
          )}

          {editing && form.role === "staff" && (
            <div data-testid="user-staff-location-wrap">
              <label className="text-xs text-[#4A5D6B] mb-1 block">Assigned center — staff only see this center in the POS register</label>
              <select value={form.staff_location_id} onChange={(e) => set("staff_location_id", e.target.value)} data-testid="user-staff-location" className={inp}>
                <option value="">Not assigned (no POS access)</option>
                {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {(form.role === "enhancer" || form.can_publish) && (
            <div data-testid="user-hourly-rate-wrap">
              <label className="text-xs text-[#4A5D6B] mb-1 block">Hourly pay rate ($/hr) — overrides the location default in payroll</label>
              <input type="number" min="0" step="0.5" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} data-testid="user-hourly-rate" className={inp} />
            </div>
          )}

          <button onClick={save} disabled={saving || (!editing && !form.email)} data-testid="user-save" className="eyw-btn w-full mt-2 disabled:opacity-60">
            {saving && <Loader2 className="animate-spin" size={18} />} <Save size={15} /> {editing ? "Save changes" : "Create user"}
          </button>
        </div>
        {editing && <DetailsSection userId={user.id} />}
        {editing && form.role === "enhancer" && <EnhancerCustomFields user={user} />}
        {editing && <CreditsSection userId={user.id} />}
        {editing && form.role === "enhancer" && <ApprovalsSection userId={user.id} />}
        {editing && <ComplianceSection user={user} onDone={() => { onSaved(); onClose(); }} />}
      </div>
    </div>
  );
}

function ComplianceSection({ user, onDone }) {
  const [suspended, setSuspended] = useState(!!user.suspended);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");

  const suspend = async (next) => {
    if (next && !reason.trim() && !window.confirm("Suspend without a reason note? A reason is recommended for the compliance log.")) return;
    setBusy("suspend");
    try {
      await api.post(`/admin/users/${user.id}/suspend`, { suspended: next, reason });
      setSuspended(next);
      toast.success(next ? "Member suspended — login blocked & content hidden" : "Member reinstated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const purge = async () => {
    if (!reason.trim()) { toast.error("A compliance reason is required to purge."); return; }
    if (!window.confirm(`PURGE ${user.name || user.email} from the ecosystem?\n\nThis permanently removes their account and public content (services, products, courses, media) and releases any center they manage. Past bookings & financial records are preserved. This cannot be undone.`)) return;
    if (!window.confirm("Final confirmation — type-safe check: this is irreversible. Purge now?")) return;
    setBusy("purge");
    try {
      const { data } = await api.post(`/admin/users/${user.id}/purge`, { reason });
      toast.success(`Purged ${data.email}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-red-200" data-testid="user-compliance-section">
      <span className="text-sm font-semibold text-red-700 flex items-center gap-1.5"><ShieldAlert size={15} /> Compliance actions</span>
      <p className="text-xs text-[#8FA89B] mb-3">Suspend (reversible) hides their content and blocks login. Purge permanently removes them for a compliance/unresolvable issue while preserving financial history. A reason is logged.</p>
      <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="compliance-reason"
        placeholder="Reason (required to purge, recommended for suspend) — e.g. repeated policy violation, unresolved dispute…"
        className="eyw-input w-full text-sm resize-none mb-3" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => suspend(!suspended)} disabled={!!busy} data-testid="compliance-suspend"
          className={`eyw-btn-ghost !py-2 text-sm ${suspended ? "!text-[#5C8A72]" : "!text-[#B5794E]"} disabled:opacity-60`}>
          {busy === "suspend" ? <Loader2 className="animate-spin" size={14} /> : <Ban size={14} />} {suspended ? "Reinstate member" : "Suspend member"}
        </button>
        <button onClick={purge} disabled={!!busy} data-testid="compliance-purge"
          className="eyw-btn !py-2 text-sm !bg-red-600 hover:!bg-red-700 disabled:opacity-60">
          {busy === "purge" ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Purge from ecosystem
        </button>
      </div>
      {suspended && <p className="text-xs text-[#B5794E] mt-2" data-testid="compliance-status">This member is currently suspended.</p>}
    </div>
  );
}

function EnhancerCustomFields({ user }) {
  const [values, setValues] = useState(user.custom_fields || {});
  const [saving, setSaving] = useState(false);
  const { data: fields = [] } = useQuery({
    queryKey: ["cf-enhancer"],
    queryFn: () => api.get("/custom-fields?entity=enhancer").then((r) => r.data),
  });
  if (fields.length === 0) return null;

  const save = async (next) => {
    setValues(next);
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/custom-fields`, { custom_fields: next });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="enhancer-custom-fields">
      <span className="text-sm font-semibold text-[#1A242B]">Profile fields {saving && <Loader2 className="animate-spin inline ml-1" size={12} />}</span>
      <div className="mt-3 space-y-3">
        {fields.map((f) => (
          <div key={f.id}>
            <label className="text-xs text-[#4A5D6B] mb-1 block">{f.label}</label>
            <select value={values[f.key] || ""} onChange={(e) => save({ ...values, [f.key]: e.target.value })}
              data-testid={`enhancer-cf-${f.key}`} className="eyw-input w-full text-sm">
              <option value="">—</option>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApprovalsSection({ userId }) {
  const qc = useQueryClient();
  const { data: approvals = [] } = useQuery({ queryKey: ["enh-approvals", userId], queryFn: () => api.get(`/admin/enhancer-approvals?enhancer_id=${userId}`).then((r) => r.data) });
  const { data: centerServices = [] } = useQuery({ queryKey: ["center-services"], queryFn: () => api.get("/center-services").then((r) => r.data) });
  const [svcId, setSvcId] = useState("");
  const [price, setPrice] = useState("");
  const [pct, setPct] = useState(70);
  const [fee, setFee] = useState(0);
  const [bufAfter, setBufAfter] = useState(0);
  const [bufBefore, setBufBefore] = useState(0);
  const [room, setRoom] = useState("");
  const [virtual, setVirtual] = useState(false);
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!svcId) { toast.error("Pick a center service"); return; }
    setSaving(true);
    try {
      await api.post("/admin/enhancer-approvals", {
        enhancer_id: userId, service_id: svcId, approved: true,
        location_ids: room ? [room] : [], price_override: price === "" ? null : parseFloat(price),
        enhancer_pct: Number(pct) || 70, finders_fee_pct: Number(fee) || 0,
        buffer_after_min: Number(bufAfter) || 0, buffer_before_min: Number(bufBefore) || 0, room, can_virtual: virtual,
      });
      toast.success("Approval saved");
      setSvcId(""); setPrice(""); setRoom("");
      qc.invalidateQueries({ queryKey: ["enh-approvals", userId] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/enhancer-approvals/${id}`); qc.invalidateQueries({ queryKey: ["enh-approvals", userId] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="user-approvals-section">
      <span className="text-sm font-semibold text-[#1A242B]">Center service approvals</span>
      <p className="text-xs text-[#8FA89B] mb-3">Approve this enhancer to offer official center services (official names) at approved locations, with per-approval pricing & finder's fee.</p>
      <div className="space-y-1.5 mb-3">
        {approvals.map((a) => (
          <div key={a.id} className="text-xs flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-[#E5E0D8]" data-testid={`approval-${a.id}`}>
            <span className="text-[#1A242B]">{a.service_title} <span className="text-[#8FA89B]">· {a.reporting_category}{a.price_override != null ? ` · ${money(a.price_override)}` : ""}{a.can_virtual ? " · virtual" : ""}{a.room ? ` · ${a.room}` : ""} · fee ${a.finders_fee_pct}%</span></span>
            <button onClick={() => del(a.id)} data-testid={`approval-del-${a.id}`} className="text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        {approvals.length === 0 && <p className="text-xs text-[#8FA89B]">No approvals yet.</p>}
      </div>
      <div className="rounded-xl bg-white border border-[#E5E0D8] p-3 space-y-2">
        <select value={svcId} onChange={(e) => setSvcId(e.target.value)} data-testid="approval-service" className="eyw-input w-full text-sm">
          <option value="">Select a center service…</option>
          {centerServices.map((s) => <option key={s.id} value={s.id}>{s.title} ({s.reporting_category})</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="approval-price" placeholder="Price override" className="eyw-input text-sm w-28" />
          <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="Enh %" title="Enhancer %" className="eyw-input text-sm w-20" />
          <input type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="Finder %" title="Finder's fee %" className="eyw-input text-sm w-20" />
          <input value={room} onChange={(e) => setRoom(e.target.value)} data-testid="approval-room" placeholder="room key" className="eyw-input text-sm w-24" />
          <input type="number" value={bufBefore} onChange={(e) => setBufBefore(e.target.value)} data-testid="approval-buf-before" placeholder="Buf before" title="Prep buffer before (min)" className="eyw-input text-sm w-20" />
          <input type="number" value={bufAfter} onChange={(e) => setBufAfter(e.target.value)} data-testid="approval-buf-after" placeholder="Buf after" title="Cleaning buffer after (min)" className="eyw-input text-sm w-20" />
          <label className="flex items-center gap-1.5 text-xs text-[#4A5D6B]"><input type="checkbox" checked={virtual} onChange={(e) => setVirtual(e.target.checked)} data-testid="approval-virtual" /> Virtual</label>
        </div>
        <button onClick={add} disabled={saving} data-testid="approval-add" className="eyw-btn !py-2 text-sm w-full">Approve service</button>
      </div>
    </div>
  );
}

function DetailsSection({ userId }) {
  const [showRaw, setShowRaw] = useState(false);
  const { data } = useQuery({ queryKey: ["admin-user-detail", userId], queryFn: () => api.get(`/admin/users/${userId}`).then((r) => r.data) });
  const u = data?.user || {};
  const rows = [
    ["Email", u.email],
    ["Phone", u.phone || u.alt_phone],
    ["Address", [u.address, u.city, u.state, u.zip, u.country].filter(Boolean).join(", ")],
    ["Timezone", u.timezone],
    ["Signup source", u.signup_source],
    ["Joined", String(u.created_at || "").slice(0, 10)],
    ["Bookings", data?.bookings_count],
    ["Agreements signed", data?.signed_agreements?.length],
    ["Email opt-in", u.email_opt_in === false ? "No" : "Yes"],
    ["SMS opt-in", u.sms_opt_in ? "Yes" : "No"],
    ["CRM pathway", u.crm_pathway],
    ["CRM stage", u.crm_stage],
    ["CRM status", u.crm_status],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");
  const wp = u.wp_meta && typeof u.wp_meta === "object" ? u.wp_meta : {};
  const wpKeys = Object.keys(wp).filter((k) => wp[k] !== "" && wp[k] != null);

  return (
    <div className="mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="user-details-section">
      <span className="text-sm font-semibold text-[#1A242B]">Member details</span>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wide text-[#8FA89B]">{k}</dt>
            <dd className="text-sm text-[#1A242B] truncate" title={String(v)} data-testid={`user-detail-${k.toLowerCase().replace(/\s+/g, "-")}`}>{String(v)}</dd>
          </div>
        ))}
      </dl>
      {(data?.signed_agreements || []).length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-wide text-[#8FA89B] mb-1">Agreements</p>
          <div className="space-y-1">
            {data.signed_agreements.map((s) => (
              <div key={s.id} className="text-xs text-[#4A5D6B] bg-white rounded-lg px-3 py-1.5 border border-[#E5E0D8] flex justify-between">
                <span>{s.agreement_title || s.agreement_id} <span className="text-[#8FA89B]">v{s.agreement_version}</span></span>
                <span className="text-[#8FA89B]">{String(s.signed_at).slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {wpKeys.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowRaw((s) => !s)} data-testid="user-wp-toggle" className="text-xs text-[#144F75] hover:underline">
            {showRaw ? "Hide" : "Show"} imported WordPress data ({wpKeys.length})
          </button>
          {showRaw && (
            <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-[#E5E0D8] bg-white p-3 space-y-1" data-testid="user-wp-data">
              {wpKeys.map((k) => (
                <div key={k} className="text-[11px] flex gap-2">
                  <span className="text-[#8FA89B] shrink-0">{k}:</span>
                  <span className="text-[#1A242B] break-all">{String(wp[k])}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreditsSection({ userId }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["user-credits", userId], queryFn: () => api.get(`/admin/users/${userId}/credits`).then((r) => r.data) });
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("comp");
  const [reason, setReason] = useState("");
  const [sign, setSign] = useState(1);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = (parseFloat(amount) || 0) * sign;
    if (!amt) { toast.error("Enter an amount"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/users/${userId}/credits`, { amount: amt, type, reason });
      toast.success("Credit updated");
      setAmount(""); setReason("");
      qc.invalidateQueries({ queryKey: ["user-credits", userId] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="user-credits-section">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#1A242B]">Account credit / comps</span>
        <span className="text-lg font-serif text-[#5C8A72]" data-testid="user-credit-balance">{money(Number(data?.balance || 0))}</span>
      </div>
      <div className="flex flex-wrap gap-2 items-end mb-3">
        <select value={sign} onChange={(e) => setSign(Number(e.target.value))} data-testid="credit-sign" className="eyw-input text-sm">
          <option value={1}>Add</option><option value={-1}>Remove</option>
        </select>
        <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credit-amount" placeholder="$ amount" className="eyw-input text-sm w-24" />
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="credit-type" className="eyw-input text-sm">
          {["comp", "scholarship", "refund", "service_credit", "trade", "adjustment"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="credit-reason" placeholder="Reason (internal note)" className="eyw-input text-sm flex-1 min-w-[140px]" />
        <button onClick={submit} disabled={saving} data-testid="credit-apply" className="eyw-btn !py-2 text-sm">Apply</button>
      </div>
      <div className="max-h-40 overflow-y-auto space-y-1.5">
        {(data?.ledger || []).map((l) => (
          <div key={l.id} className="text-xs flex items-center justify-between bg-white rounded-lg px-3 py-1.5 border border-[#E5E0D8]">
            <span className="text-[#4A5D6B]"><span className={l.amount >= 0 ? "text-[#5C8A72]" : "text-[#B5794E]"}>{l.amount >= 0 ? "+" : ""}{Number(l.amount).toFixed(2)}</span> · {l.type}{l.reason ? ` · ${l.reason}` : ""}</span>
            <span className="text-[#8FA89B]">{String(l.created_at).slice(0, 10)}</span>
          </div>
        ))}
        {data?.ledger?.length === 0 && <p className="text-xs text-[#8FA89B]">No credit history yet.</p>}
      </div>
    </div>
  );
}
